import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useTenant } from '../../providers/TenantProvider';
import { appointmentConverter } from '../../lib/db/converters';
import type { Appointment, AppointmentStatus, Service, User } from '../../lib/db/types';

interface AppointmentWithId extends Appointment {
  id: string;
}

interface ServiceWithId extends Service {
  id: string;
}

interface ProviderInfo {
  id: string;
  name: string;
  photoUrl?: string;
}

interface MemberWithId {
  id: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
}

const STATUS_CONFIG: Record<AppointmentStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  confirmed: { label: 'Confirmed', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  completed: { label: 'Completed', color: 'info' },
  canceled: { label: 'Canceled', color: 'error' },
  no_show: { label: 'No Show', color: 'default' },
};

function formatDate(timestamp: Timestamp): string {
  if (!timestamp?.toDate) return '—';
  return timestamp.toDate().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timestamp: Timestamp): string {
  if (!timestamp?.toDate) return '—';
  return timestamp.toDate().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isUpcoming(appointment: Appointment): boolean {
  return appointment.startTime.toMillis() > Date.now() && appointment.status !== 'canceled';
}

function isPast(appointment: Appointment): boolean {
  return appointment.endTime.toMillis() < Date.now() || appointment.status === 'completed' || appointment.status === 'canceled';
}

export function MemberAppointmentsPage() {
  const navigate = useNavigate();
  const { user, tenantId, activeMembership } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [memberId, setMemberId] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<AppointmentWithId[]>([]);
  const [services, setServices] = useState<Map<string, ServiceWithId>>(new Map());
  const [providers, setProviders] = useState<Map<string, ProviderInfo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<AppointmentWithId | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Load member data and appointments
  useEffect(() => {
    if (!user || !tenantId) {
      setError('Please sign in to view your appointments');
      setLoading(false);
      return;
    }

    if (!activeMembership?.memberId) {
      setError('Member record not found. Please contact support.');
      setLoading(false);
      return;
    }
    const currentMemberId = activeMembership.memberId;

    const loadData = async () => {
      try {
        const memberDoc = await getDoc(doc(db, 'members', currentMemberId));
        if (!memberDoc.exists()) {
          setError('Member record not found. Please contact support.');
          setLoading(false);
          return;
        }

        const memberData: MemberWithId = {
          id: memberDoc.id,
          contact: memberDoc.data().contact,
        };
        setMemberId(memberData.id);

        // Load all appointments for this member
        const appointmentsQuery = query(
          collection(db, 'appointments').withConverter(appointmentConverter),
          where('tenantId', '==', tenantId),
          where('customerId', '==', memberData.id),
          where('customerType', '==', 'member'),
          orderBy('startTime', 'desc')
        );
        const appointmentsSnap = await getDocs(appointmentsQuery);
        const appointmentsData = appointmentsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as AppointmentWithId[];
        setAppointments(appointmentsData);

        // Load services for these appointments
        const serviceIds = [...new Set(appointmentsData.map((a) => a.serviceId))];
        const servicesMap = new Map<string, ServiceWithId>();
        for (const serviceId of serviceIds) {
          const serviceDoc = await getDocs(query(
            collection(db, 'services'),
            where('__name__', '==', serviceId)
          ));
          if (!serviceDoc.empty) {
            const doc = serviceDoc.docs[0];
            servicesMap.set(serviceId, { id: doc.id, ...doc.data() } as ServiceWithId);
          }
        }
        setServices(servicesMap);

        // Load providers for these appointments
        const providerIds = [...new Set(appointmentsData.map((a) => a.providerId))];
        const providersMap = new Map<string, ProviderInfo>();
        for (const providerId of providerIds) {
          const providerDoc = await getDocs(query(
            collection(db, 'tenants', tenantId, 'memberships'),
            where('__name__', '==', providerId)
          ));
          if (!providerDoc.empty) {
            const doc = providerDoc.docs[0];
            const data = doc.data() as User;
            const fullName = [data.firstName, data.lastName]
              .filter(Boolean)
              .join(' ') || 'Unnamed Provider';
            providersMap.set(providerId, {
              id: doc.id,
              name: fullName,
              photoUrl: data.photoUrl,
            });
          }
        }
        setProviders(providersMap);
      } catch (err: any) {
        console.error('Error loading appointments:', err);
        setError('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeMembership?.memberId, tenantId, user]);

  const handleCancelClick = (appointment: AppointmentWithId) => {
    setAppointmentToCancel(appointment);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;

    try {
      setCancelling(true);
      setError(null);

      const cancelAppointment = httpsCallable(functions, 'cancelAppointment');
      await cancelAppointment({ appointmentId: appointmentToCancel.id });

      // Update local state
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appointmentToCancel.id ? { ...a, status: 'canceled' } : a
        )
      );

      setSuccess('Appointment cancelled successfully');
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
    } catch (err: any) {
      console.error('Error cancelling appointment:', err);
      setError(err.message || 'Failed to cancel appointment');
    } finally {
      setCancelling(false);
    }
  };

  const handleReschedule = (appointment: AppointmentWithId) => {
    navigate('/member/book', {
      state: {
        appointmentId: appointment.id,
        serviceId: appointment.serviceId,
        providerId: appointment.providerId,
      },
    });
  };

  const getService = (id: string) => services.get(id);
  const getProvider = (id: string) => providers.get(id);

  const upcomingAppointments = appointments.filter(isUpcoming);
  const pastAppointments = appointments.filter(isPast);

  const displayedAppointments = activeTab === 0 ? upcomingAppointments : pastAppointments;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !memberId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" component="h1" fontWeight={600}>
                My Appointments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage your upcoming appointments
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/member/book')}
              startIcon={<AddIcon />}
            >
              Book Appointment
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, bgcolor: 'white', borderRadius: 1 }}
        >
          <Tab
            label={`Upcoming (${upcomingAppointments.length})`}
            icon={<ScheduleIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Past (${pastAppointments.length})`}
            icon={<CheckCircleIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Appointments List */}
        {displayedAppointments.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              {activeTab === 0 ? (
                <>
                  <ScheduleIcon
                    sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No upcoming appointments
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You don't have any upcoming appointments scheduled.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/member/book')}
                    startIcon={<AddIcon />}
                  >
                    Book an Appointment
                  </Button>
                </>
              ) : (
                <>
                  <CheckCircleIcon
                    sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No past appointments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your appointment history will appear here.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {displayedAppointments.map((appointment) => {
              const service = getService(appointment.serviceId);
              const provider = getProvider(appointment.providerId);
              const statusConfig = STATUS_CONFIG[appointment.status];
              const canCancel =
                isUpcoming(appointment) &&
                appointment.status !== 'canceled';
              const canReschedule =
                isUpcoming(appointment) &&
                appointment.status !== 'canceled';

              return (
                <Card
                  key={appointment.id}
                  sx={{
                    borderLeft: 4,
                    borderLeftColor:
                      appointment.status === 'canceled'
                        ? 'error.main'
                        : service?.color || theme.palette.primary.main,
                    opacity: appointment.status === 'canceled' ? 0.7 : 1,
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Grid container spacing={2} alignItems="center">
                      {/* Date Column */}
                      <Grid item xs={12} sm={3} md={2}>
                        <Box
                          sx={{
                            textAlign: { xs: 'left', sm: 'center' },
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            color="primary.main"
                          >
                            {appointment.startTime.toDate().getDate()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {appointment.startTime.toDate().toLocaleDateString('en-US', {
                              month: 'short',
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {appointment.startTime.toDate().toLocaleDateString('en-US', {
                              weekday: 'short',
                            })}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Details Column */}
                      <Grid item xs={12} sm={6} md={7}>
                        <Box>
                          {/* Service Name */}
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            {service?.name || 'Unknown Service'}
                          </Typography>

                          {/* Provider */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                              color: 'text.secondary',
                            }}
                          >
                            <PersonIcon fontSize="small" />
                            <Typography variant="body2">
                              {provider?.name || 'Unknown Provider'}
                            </Typography>
                          </Box>

                          {/* Time */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                              color: 'text.secondary',
                            }}
                          >
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="body2">
                              {formatTime(appointment.startTime)} -{' '}
                              {formatTime(appointment.endTime)}
                            </Typography>
                          </Box>

                          {/* Status */}
                          <Chip
                            size="small"
                            color={statusConfig.color}
                            label={statusConfig.label}
                            sx={{ mt: 1 }}
                          />

                          {/* Notes */}
                          {appointment.notes && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1, fontStyle: 'italic' }}
                            >
                              Note: {appointment.notes}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Actions Column */}
                      <Grid item xs={12} sm={3} md={3}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'column' },
                            gap: 1,
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                          }}
                        >
                          {canReschedule && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleReschedule(appointment)}
                              fullWidth={!isMobile}
                            >
                              Reschedule
                            </Button>
                          )}
                          {canCancel && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelClick(appointment)}
                              fullWidth={!isMobile}
                            >
                              Cancel
                            </Button>
                          )}
                          {!canCancel && !canReschedule && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="right"
                            >
                              {appointment.status === 'completed'
                                ? 'Completed'
                                : appointment.status === 'canceled'
                                ? 'Cancelled'
                                : ''}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your appointment for{' '}
            <Box component="strong" sx={{ fontWeight: 700 }}>
              {appointmentToCancel &&
                getService(appointmentToCancel.serviceId)?.name}
            </Box>{' '}
            on{' '}
            <Box component="strong" sx={{ fontWeight: 700 }}>
              {appointmentToCancel && formatDate(appointmentToCancel.startTime)}
            </Box>{' '}
            at{' '}
            <Box component="strong" sx={{ fontWeight: 700 }}>
              {appointmentToCancel && formatTime(appointmentToCancel.startTime)}
            </Box>
            ?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={cancelling}
          >
            Keep Appointment
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MemberAppointmentsPage;
